import { createSelector } from "@reduxjs/toolkit";
import React, { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { IUniversalItem } from "userful-chronos-app-common-js/dist/itemsControl/itemsControl";
import { ClickAndDragZoneContext } from "../clickAndDragZone/ClickAndDragZone";

import {
    ItemsControlActions,
    useItemsControlDispatch,
    useItemsControlSelector,
} from "userful-chronos-common-store/dist/itemsControl";
import "./ClickAndDragItem.scss";

interface IProps {
    item: IUniversalItem;
    full?: boolean;
    styles?: React.CSSProperties;
    children?: React.ReactNode;
}

const DRAG_START_THRESHOLD = 5;
const IDLE_OVER_DROPPABLE_DELEY = 1000; // ms

// Shared ref so any ClickAndDragItem can write the latest dropzone synchronously,
// and all instances (including the one handling pointerup) can read it.
const globalOverDropzoneRef: { current: IUniversalItem | null } = { current: null };

export default function ClickAndDragItem(props: IProps) {
    const zoneContext = useContext(ClickAndDragZoneContext);
    const dispatch = useItemsControlDispatch();

    const itemRef = useRef<HTMLDivElement | null>(null);
    const pointerIdRef = useRef<number | null>(null);
    const startPointRef = useRef<{ x: number; y: number } | null>(null);
    const dragStartedRef = useRef(false);
    const rafRef = useRef<number | null>(null);
    const lastPointerRef = useRef<{ x: number; y: number } | null>(null);
    const moveHandlerRef = useRef<((event: PointerEvent) => void) | undefined>();
    const upHandlerRef = useRef<((event: PointerEvent) => void) | undefined>();
    const suppressClickRef = useRef(false);
    const isSelectable = props.item.selectable !== false;
    const startMetaRef = useRef<{
        offset: { x: number; y: number };
        ratio: { x: number; y: number };
        rect: { width: number; height: number };
        useInitialSize: boolean;
    } | null>(null);
    const activeDraggedItemsRef = useRef<IUniversalItem[]>([]);

    const selectionStateSelector = useMemo(
        () =>
            createSelector(
                [
                    (state) => state.itemsControl.preselectedItems,
                    (state) => state.itemsControl.selectedItems,
                    (state) => state.itemsControl.draggedItems,
                ],
                (preselectedItems, selectedItems, draggedItems) => {
                    const filteredPreselectedItems = preselectedItems.filter((item) => item.selectable !== false);
                    const filteredSelectedItems = selectedItems.filter((item) => item.selectable !== false);
                    const preselected =
                        isSelectable && filteredPreselectedItems.some((item) => item.id === props.item.id);
                    const selected = isSelectable && filteredSelectedItems.some((item) => item.id === props.item.id);
                    const dragging = draggedItems.some((item) => item.id === props.item.id);
                    return { preselected, selected, selectedItems: filteredSelectedItems, dragging };
                },
            ),
        [isSelectable, props.item.id],
    );

    const dragMetaStateSelector = useMemo(
        () =>
            createSelector(
                [
                    (state) => state.itemsControl.draggingType,
                    (state) => state.itemsControl.draggedItems,
                    (state) => state.itemsControl.overDropzone,
                ],
                (draggingType, draggedItems, overDropzone) => ({ draggingType, draggedItems, overDropzone }),
            ),
        [],
    );

    const selectionState = useItemsControlSelector(selectionStateSelector);
    const dragMetaState = useItemsControlSelector(dragMetaStateSelector);

    const dragState = useItemsControlSelector((state) => state.itemsControl.drag);
    const activeDragId = dragState.primaryId;
    const { draggingType, draggedItems, overDropzone } = dragMetaState;

    const hasActiveDrag = draggingType === "MOVE" || draggingType === "COPY";
    const isSelected = selectionState.preselected || selectionState.selected;
    const isDragging = selectionState.dragging;
    const isOverCurrentDropzone = overDropzone?.id === props.item.id;

    useEffect(() => {
        // console.log("Updating overDropzoneRef:", overDropzone);
        globalOverDropzoneRef.current = overDropzone;
    }, [overDropzone]);

    const canAcceptDrop = useCallback((candidate: IUniversalItem, activeDragged: IUniversalItem[]) => {
        if (!candidate || candidate.droppable === false) {
            return false;
        }
        if (!activeDragged.length) {
            return false;
        }
        if (activeDragged.some((item) => item.id === candidate.id)) {
            return false;
        }
        const accepted = candidate.acceptedTypes;
        if (accepted && accepted.length) {
            return activeDragged.every((item) => accepted.includes(item.type));
        }
        return true;
    }, []);

    const cleanupListeners = useCallback(() => {
        if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        if (moveHandlerRef.current) {
            window.removeEventListener("pointermove", moveHandlerRef.current);
            moveHandlerRef.current = undefined;
        }
        if (upHandlerRef.current) {
            window.removeEventListener("pointerup", upHandlerRef.current);
            window.removeEventListener("pointercancel", upHandlerRef.current);
            upHandlerRef.current = undefined;
        }
        pointerIdRef.current = null;
        startPointRef.current = null;
        lastPointerRef.current = null;
        startMetaRef.current = null;
        dragStartedRef.current = false;
        activeDraggedItemsRef.current = [];
    }, []);

    const handleClick = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (!isSelectable) {
                return;
            }
            if (suppressClickRef.current) {
                suppressClickRef.current = false;
                return;
            }
            if (event.button !== 0) {
                return;
            }
            const ctrlPressed = event.ctrlKey;

            if (ctrlPressed) {
                const alreadySelected = selectionState.selectedItems.some(
                    (item) => item.id === props.item.id, // or === if primitives
                );

                const nextSelected = alreadySelected
                    ? selectionState.selectedItems.filter((item) => item.id !== props.item.id)
                    : [...selectionState.selectedItems, props.item];

                dispatch(ItemsControlActions.updateSelectedItems(nextSelected));
            } else {
                dispatch(ItemsControlActions.updateSelectedItems([props.item]));
            }

            dispatch(ItemsControlActions.updatePreselectedItems([]));
            dispatch(ItemsControlActions.updateMarquee(null));
        },
        [dispatch, isSelectable, props.item, selectionState.selectedItems],
    );

    const startDragging = useCallback(
        (pointerEvent: PointerEvent) => {
            if (dragStartedRef.current) {
                return;
            }
            if (props.item.draggable === false) {
                return;
            }

            const inSelection = selectionState.selectedItems.some((item) => item.id === props.item.id);
            const baseItems = inSelection ? selectionState.selectedItems : [props.item];

            const itemsMap = new Map<string, IUniversalItem>();
            baseItems.forEach((item) => {
                if (item.draggable === false) {
                    return;
                }
                itemsMap.set(item.id, item);
            });

            itemsMap.delete(props.item.id);
            itemsMap.set(props.item.id, props.item);

            const dragItems = Array.from(itemsMap.values());
            if (!dragItems.length) {
                return;
            }

            dragStartedRef.current = true;
            suppressClickRef.current = true;
            activeDraggedItemsRef.current = dragItems;

            dragItems.forEach((item) => item.onDragStart?.());

            const primary = dragItems.find((item) => item.id === props.item.id) ?? dragItems[0];
            const meta = startMetaRef.current;
            const flyContent = primary.replaceFly ?? props.item.replaceFly ?? props.children ?? null;

            dispatch(
                ItemsControlActions.startDrag({
                    items: dragItems,
                    primaryId: primary.id,
                    fly: flyContent,
                    pointer: { x: pointerEvent.clientX, y: pointerEvent.clientY },
                    offset: meta?.offset ?? null,
                    ratio: meta?.ratio ?? null,
                    initialSize: meta?.rect ?? null,
                    useInitialSize: meta?.useInitialSize ?? !primary.replaceFly,
                }),
            );
        },
        [dispatch, props.children, props.item, selectionState.selectedItems],
    );

    const handlePointerDown = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (event.pointerType === "mouse" && event.button !== 0) {
                return;
            }
            if (activeDragId) {
                return;
            }
            if (props.item.draggable === false) {
                return;
            }

            pointerIdRef.current = event.pointerId;
            startPointRef.current = { x: event.clientX, y: event.clientY };
            suppressClickRef.current = false;
            dragStartedRef.current = false;

            const rect = itemRef.current?.getBoundingClientRect();
            if (rect) {
                const offset = { x: event.clientX - rect.left, y: event.clientY - rect.top };
                const ratio = {
                    x: rect.width ? offset.x / rect.width : 0.5,
                    y: rect.height ? offset.y / rect.height : 0.5,
                };
                startMetaRef.current = {
                    offset,
                    ratio,
                    rect: { width: rect.width, height: rect.height },
                    useInitialSize: !props.item.replaceFly,
                };
            } else {
                startMetaRef.current = null;
            }

            const handleMove = (pointerEvent: PointerEvent) => {
                if (pointerIdRef.current !== null && pointerEvent.pointerId !== pointerIdRef.current) {
                    return;
                }
                lastPointerRef.current = { x: pointerEvent.clientX, y: pointerEvent.clientY };

                if (!dragStartedRef.current) {
                    const start = startPointRef.current;
                    if (start) {
                        const dx = pointerEvent.clientX - start.x;
                        const dy = pointerEvent.clientY - start.y;
                        const distance = Math.hypot(dx, dy);
                        if (distance >= DRAG_START_THRESHOLD) {
                            startDragging(pointerEvent);
                            pointerEvent.preventDefault();
                        }
                    }
                    return;
                }

                if (rafRef.current === null) {
                    rafRef.current = requestAnimationFrame(() => {
                        rafRef.current = null;
                        if (lastPointerRef.current) {
                            dispatch(ItemsControlActions.updatePointer(lastPointerRef.current));
                        }
                    });
                }
                activeDraggedItemsRef.current.forEach((item) => item.onDrag?.());
            };

            const handleUp = (pointerEvent: PointerEvent) => {
                if (pointerIdRef.current !== null && pointerEvent.pointerId !== pointerIdRef.current) {
                    return;
                }
                const started = dragStartedRef.current;
                const draggedItems = activeDraggedItemsRef.current;

                // Read the latest drop target synchronously to avoid effect timing issues
                const dropTarget = globalOverDropzoneRef.current;

                console.log("handleUp - dropTarget:", dropTarget);
                console.log("handleUp - canAcceptDrop:", dropTarget ? canAcceptDrop(dropTarget, draggedItems) : false);

                cleanupListeners();

                if (started) {
                    draggedItems.forEach((item) => item.onDragStop?.());
                    draggedItems.forEach((item) => item.onDraggableDrop?.());

                    if (dropTarget && canAcceptDrop(dropTarget, draggedItems)) {
                        dropTarget.onDroppableDrop?.(draggedItems);
                    }
                    globalOverDropzoneRef.current = null;
                    dispatch(ItemsControlActions.updateOverDropzone(null));
                    activeDraggedItemsRef.current = [];
                    dispatch(ItemsControlActions.endDrag());
                    suppressClickRef.current = true;
                }
            };

            moveHandlerRef.current = handleMove;
            upHandlerRef.current = handleUp;

            window.addEventListener("pointermove", handleMove);
            window.addEventListener("pointerup", handleUp);
            window.addEventListener("pointercancel", handleUp);
        },
        [
            activeDragId,
            canAcceptDrop,
            cleanupListeners,
            dispatch,
            props.item.draggable,
            props.item.replaceFly,
            startDragging,
        ],
    );

    const handlePointerOver = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (!hasActiveDrag) {
                return;
            }

            // Ignore pointer transitions inside the same droppable
            const cameFromInside = event.relatedTarget && event.currentTarget.contains(event.relatedTarget as Node);
            if (cameFromInside && overDropzone?.id === props.item.id) {
                return;
            }

            if (!canAcceptDrop(props.item, draggedItems)) {
                return;
            }
            if (overDropzone?.id === props.item.id) {
                return;
            }
            // Update the ref immediately so handleUp sees the latest target even before effects run
            globalOverDropzoneRef.current = props.item;
            dispatch(ItemsControlActions.updateOverDropzone(props.item));
        },
        [canAcceptDrop, dispatch, draggedItems, hasActiveDrag, overDropzone?.id, props.item],
    );

    const handlePointerOut = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (overDropzone?.id !== props.item.id) {
                return;
            }
            // If moving to another element inside the current dropzone, keep the highlight.
            const movingInside = event.relatedTarget && event.currentTarget.contains(event.relatedTarget as Node);
            if (movingInside) {
                return;
            }
            globalOverDropzoneRef.current = null;
            dispatch(ItemsControlActions.updateOverDropzone(null));
        },
        [dispatch, overDropzone?.id, props.item.id],
    );

    useEffect(() => {
        if (!zoneContext) {
            return;
        }

        zoneContext.registerItem(props.item, itemRef);

        return () => {
            zoneContext.unregisterItem(props.item.id);
        };
    }, [props.item, zoneContext]);

    useEffect(() => {
        return () => {
            if (dragStartedRef.current) {
                // Keep drag state alive across transient unmounts (e.g. hover navigation); pointerup handler will finish the drag.
                return;
            }
            cleanupListeners();
        };
    }, [cleanupListeners]);

    useEffect(() => {
        if (!hasActiveDrag || !isOverCurrentDropzone || !props.item.onIdle) {
            return;
        }
        const timerId = window.setTimeout(() => {
            props.item.onIdle?.();
        }, IDLE_OVER_DROPPABLE_DELEY);
        return () => {
            window.clearTimeout(timerId);
        };
    }, [hasActiveDrag, isOverCurrentDropzone, props.item]);

    const className = useMemo(() => {
        const classes = [];
        if (props.item.draggable !== false) {
            classes.push("drag-and-drop-draggable");
        }
        const baseClass = props.full ? "click-and-drag-item-full" : "click-and-drag-item";
        classes.push(baseClass);

        if (isSelected) {
            classes.push("click-and-drag-item-selected");
        }
        if (isDragging) {
            classes.push("drag-and-drop-draggable-dragging");
        }
        if (isOverCurrentDropzone) {
            classes.push("drag-and-drop-droppable-over");
        }

        return classes.join(" ");
    }, [isDragging, isOverCurrentDropzone, isSelected, props.full, props.item.draggable]);

    return (
        <div
            id={props.item.id}
            ref={itemRef}
            className={className}
            data-selected={isSelected}
            data-preselected={selectionState.preselected}
            data-dragging={isDragging}
            data-click-and-drag-item-id={props.item.id}
            onPointerDown={handlePointerDown}
            onPointerOver={handlePointerOver}
            onPointerOut={handlePointerOut}
            onClick={handleClick}
            style={props.styles}
        >
            {props.children}
        </div>
    );
}
